import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = '/userGroup';

export interface UseLoadAllUserGroupsOptions {
	pageSize?: number;
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
	expand?: string;
}

export function useLoadAllUserGroups({
	pageSize = 500,
	impersonationContext,
	enabled = true,
	expand,
}: UseLoadAllUserGroupsOptions = {}) {
	const userSession = useUserSession();

	const query = useInfiniteQuery({
		enabled,
		queryFn: async ({ pageParam }) => {
			const queryParams: any = { pageSize, pageToken: pageParam };
			if (expand) {
				queryParams.expand = expand;
			}

			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IUserGroup>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams,
					urlPath: `userGroup/`,
				}),
				'GET',
				null
			);
		},
		getNextPageParam: lastPage => lastPage.pageToken,
		queryKey: [BASE_QUERY_KEY, impersonationContext, pageSize, expand],
		initialPageParam: '' as string | undefined,
	});

	// Flatten all pages into a single array of user groups
	const userGroups = query.data?.pages.flatMap(page => page.values || []) || [];

	return {
		userGroups,
		isLoading: query.isLoading,
		isFetching: query.isFetching,
		isError: query.isError,
		error: query.error,
		hasNextPage: query.hasNextPage,
		fetchNextPage: query.fetchNextPage,
		isFetchingNextPage: query.isFetchingNextPage,
		refetch: query.refetch,
	};
}

export const invalidateUserGroups = () => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
};
